/**
 * The hot seat: everyone round one screen, no server.
 *
 * The seat on show is whoever must decide, so the screen hands itself from
 * player to player as the game asks. Between two seats it goes behind a
 * curtain, because the cards in a hand are secret and a shared screen is the
 * one place that is easy to forget.
 *
 * The game lives in the URL — `?hotseat=<seed>&players=N` — so a game can be
 * started again from the beginning, which is what makes this the client's
 * end-to-end test as well as a way to play.
 */
import { decisionMaker, type PlayerCount, type PlayerId } from '@trajan/engine';
import type { SeatInfo } from '@trajan/protocol';
import { useState } from 'react';
import { hotSeatView, playHotSeat, seatToPlay, startHotSeat, type HotSeat } from './hotseat';
import { TopBar } from './TopBar';

/** Seats are known by their colour at a table, which is what the dots show. */
export const SEAT_NAMES = ['Red', 'Green', 'Blue', 'Brown'];
export const seatName = (seat: PlayerId): string => SEAT_NAMES[seat] ?? `seat ${seat}`;

export interface HotSeatSetup {
  seed: string;
  playerCount: PlayerCount;
  /** Hide the screen when it changes hands. Off with `&curtain=0`, for solo play. */
  curtain: boolean;
}

/** `?hotseat=<seed>&players=N&curtain=0`, or null when this is not a hot-seat page. */
export function hotSeatFromLocation(search: string): HotSeatSetup | null {
  const params = new URLSearchParams(search);
  if (!params.has('hotseat')) return null;
  const asked = Number(params.get('players') ?? 3);
  return {
    seed: params.get('hotseat') || 'hotseat',
    playerCount: (asked === 2 || asked === 3 || asked === 4 ? asked : 3) as PlayerCount,
    curtain: params.get('curtain') !== '0',
  };
}

export function HotSeatScreen({
  setup,
  render,
}: {
  setup: HotSeatSetup;
  /** The game screen itself, which knows nothing about hot seats. */
  render: (game: {
    hs: HotSeat;
    seat: PlayerId;
    seats: SeatInfo[];
    send: (command: Parameters<typeof playHotSeat>[1]) => void;
  }) => React.ReactNode;
}) {
  const [hs, setHs] = useState<HotSeat>(() => startHotSeat(setup.seed, setup.playerCount));
  // Which seat the screen has been handed to. Null until someone takes it.
  const [handedTo, setHandedTo] = useState<PlayerId | null>(setup.curtain ? null : 0);
  const seat = seatToPlay(hs);
  const seats: SeatInfo[] = hs.state.players.map((p) => ({
    playerId: p.id,
    name: seatName(p.id),
    connected: true,
    isHost: p.id === 0,
  }));
  const behindCurtain = setup.curtain && handedTo !== seat && decisionMaker(hs.state) !== null;

  return (
    <main className="app app-game">
      <TopBar
        view={hotSeatView(hs)}
        note={
          <>
            hot seat · {setup.playerCount} players · seed <code>{setup.seed}</code>
          </>
        }
      />

      {hs.error && <p className="error">{hs.error}</p>}

      {behindCurtain ? (
        <section className="card curtain">
          <span className={`seat-dot seat-dot-${seat}`} aria-hidden="true" />
          <h2>Pass the screen to {seatName(seat)}</h2>
          <p className="muted">
            Everyone else, look away — {seatName(seat)}&rsquo;s cards are next.
          </p>
          <button className="curtain-go" onClick={() => setHandedTo(seat)}>
            I am {seatName(seat)}
          </button>
        </section>
      ) : (
        <>
          {render({
            hs,
            seat,
            seats,
            send: (command) => setHs((current) => playHotSeat(current, command)),
          })}

          {/* Behind the curtain there is nothing on screen, this included. */}
          <details className="hotseat-log">
            <summary>This game, move by move (debug)</summary>
            <p className="muted">
              The seed and these moves rebuild the game exactly — `pnpm play --replay` scores it
              again straight from the engine, which is how the screen is checked.
            </p>
            <pre>
              {JSON.stringify(
                { seed: hs.seed, playerCount: hs.playerCount, moves: hs.moves },
                null,
                1,
              )}
            </pre>
          </details>
        </>
      )}
    </main>
  );
}

/** The view for whoever holds the screen — their own cards, and nobody else's. */
export { hotSeatView };
